import { defineStore } from "pinia"

import { getParamFromUrl } from "@/services/urls.js"
import * as sentryReporter from "@/services/sentryReporter.js"

import * as messagesApi from "@/api/messagesApi.js"

import { useUserSessionStore } from "@/store/userSession.js"

export const useMessagingStore = defineStore("messaging", {
  state: () => ({
    chatsState: [],
    selectedChatState: null,
    chatsLoadingState: false,
    chatsNextCursorState: null,
    chatMessagesLoadingState: false,
    selectedNewChatContactsState: [],
    savedMessagesToSendState: [],
    messageToEditState: null,
    messageToReplyToState: null
  }),
  getters: {
    chats: (state) => state.chatsState,
    chatsLoading: (state) => state.chatsLoadingState,
    selectedChat: (state) => state.selectedChatState,
    selectedChatOtherParticipants: (state) => {
      const userSessionStore = useUserSessionStore()
      // Theoretically userSession/user should not be null, but based on our logs sometimes it is
      // https://disciple.sentry.io/issues/3964694384/events/edb9c3f829d64cfdb4f0872ddd1bba55/?project=6718259&project=4504451893624832
      return (
        state.selectedChatState?.memberships
          ?.filter((m) => m.user.id !== userSessionStore.user?.id)
          .map((m) => ({ ...m.user, blocked: m.blocked, blockable: m.blockable })) || []
      )
    },
    chatsNextCursor: (state) => state.chatsNextCursorState,
    chatMessagesLoading: (state) => state.chatMessagesLoadingState,
    selectedNewChatContacts: (state) => state.selectedNewChatContactsState,
    getSavedMessage: (state) => (conversationId) => state.savedMessagesToSendState.find((s) => s.conversationId === conversationId)?.message,
    messageToEdit: (state) => state.messageToEditState,
    messageToReplyTo: (state) => state.messageToReplyToState
  },
  actions: {
    async fetchChats() {
      if (this.chatsLoadingState) {
        return
      }

      this.chatsLoadingState = true
      try {
        const { conversations: newChats, meta } = await messagesApi.chats({ cursor: this.chatsNextCursorState })

        const validChats = newChats
          .filter((c) => !this.chatsState.find((chat) => chat.id === c.id))
          .map((chat) => ({ ...chat, title: this.getChatTitle(chat) }))

        this.chatsNextCursorState = getParamFromUrl(meta.next, "older_than")
        this.chatsState = [...this.chatsState, ...validChats]
      } catch (err) {
        sentryReporter.reportError(err)
      } finally {
        this.chatsLoadingState = false
      }
    },
    clearChats() {
      this.chatsState = []
      this.messageToEditState = null
      this.selectedChatState = null
      this.chatsNextCursorState = null
      this.chatsLoadingState = false
      this.chatMessagesLoadingState = false
    },
    getChatTitle(chat) {
      if (chat.name) {
        return chat.name
      }

      const userSessionStore = useUserSessionStore()
      const otherUsers = chat.memberships.filter((m) => m.user.id !== userSessionStore.user.id)

      if (otherUsers.length === 0) {
        return userSessionStore.user.display_name
      }
      return otherUsers.map((m) => m.user.display_name).join(", ")
    },
    clearSavedMessages() {
      this.savedMessagesToSendState = []
    },
    async selectChat(chat) {
      if (this.chatMessagesLoadingState && this.selectedChatState) {
        return
      }

      this.messageToEditState = null

      const newChatInTheList = this.chatsState.find((c) => c.new)
      if (chat.new && newChatInTheList) {
        this.selectedChatState = newChatInTheList
        return
      }

      const alreadyFetchedChat = this.chatsState.find((c) => Number(c.id) === Number(chat.id))

      if (alreadyFetchedChat) {
        Object.assign(alreadyFetchedChat, chat)
        this.selectedChatState = alreadyFetchedChat
      } else {
        try {
          this.chatMessagesLoadingState = true
          const { conversation: notYetFetchedChat } = await messagesApi.chat(chat.id)

          notYetFetchedChat.title = this.getChatTitle(notYetFetchedChat)

          this.chatsState.unshift(notYetFetchedChat)
          this.selectedChatState = notYetFetchedChat
        } catch (err) {
          sentryReporter.reportError(err)
        } finally {
          this.chatMessagesLoadingState = false
        }
      }
    },
    deselectChat() {
      this.selectedChatState = null
    },
    async createChat({ contactsIds, name }) {
      return await messagesApi.createChat({ contactsIds, name })
    },
    async createChatWithMessage({ contactsIds, name, content }) {
      const { conversation } = await messagesApi.createChat({ contactsIds, name })
      const { message } = await messagesApi.sendMessage({ conversationId: conversation.id, content })
      await this.makeTemporaryNewChatPermanent({ chatId: conversation.id })

      return { conversation, message }
    },
    async requestChatWithContacts({ contactsIds, newChatWithSelectedUser, name, immediate }) {
      this.chatMessagesLoadingState = true

      const existingConversation = await this.handleExistingConversation({ contactsIds })

      if (!existingConversation) {
        if (immediate) {
          const { conversation: chat } = await this.createChat({ contactsIds, name })
          chat.title = this.getChatTitle(chat)
          this.chatsState.unshift(chat)
          this.selectedChatState = chat
        } else {
          const contacts = this.selectedNewChatContactsState.map((contact) => ({ user: contact }))
          this.addTemporaryNewChat({ contacts, newChatWithSelectedUser, name })
          this.selectedNewChatContactsState = []
        }
      }

      this.chatMessagesLoadingState = false
    },
    async handleExistingConversation({ contactsIds }) {
      const userSessionStore = useUserSessionStore()

      const membersIds = [userSessionStore.user.id, ...contactsIds]
      const [{ conversations }, { conversations: archivedConversations }] = await Promise.all([
        messagesApi.chats({ membersIds }),
        messagesApi.chats({ membersIds, archived: true })
      ])

      const allConversations = [...conversations, ...archivedConversations]
      // Only the one where all user ids match
      const existingConversation = allConversations.find((c) => {
        const conversationMembersIds = c.memberships.map((m) => m.user.id)
        return membersIds.length === conversationMembersIds.length && membersIds.every((id) => conversationMembersIds.includes(id))
      })

      if (existingConversation) {
        if (!this.chatsState.find((c) => c.id === existingConversation.id)) {
          existingConversation.title = this.getChatTitle(existingConversation)
          this.chatsState.unshift(existingConversation)
        }
        this.selectChat(existingConversation)
      }

      return existingConversation
    },
    addTemporaryNewChat({ contacts, newChatWithSelectedUser, name }) {
      const userSessionStore = useUserSessionStore()
      const temporaryNewChat = {
        new: true,
        memberships: [{ user: userSessionStore.user }, ...contacts],
        newChatWithSelectedUser,
        name
      }

      temporaryNewChat.title = this.getChatTitle(temporaryNewChat)

      this.chatsState = [temporaryNewChat, ...this.chatsState.filter((chat) => !chat.new)]
      this.selectedChatState = temporaryNewChat
    },
    updateChat({ chatIndex, chat }) {
      if (chatIndex > -1) {
        this.chatsState.splice(chatIndex, 1)
      }

      this.chatsState.unshift(chat)
    },
    async makeTemporaryNewChatPermanent({ chatId, validateMembership }) {
      const temporaryNewChatIndex = this.chatsState.findIndex((c) => c.new)
      if (temporaryNewChatIndex < 0) {
        return
      }

      //This also refreshes chat data like last_message after sending a first message
      const { conversation: chat } = await messagesApi.chat(chatId)

      if (validateMembership) {
        // Handles scenario when both users create a new chat with each other at the same time
        const temporaryNewChat = this.chatsState[temporaryNewChatIndex]
        const temporaryNewChatMemberships = temporaryNewChat.memberships.map((m) => m.user.id)
        const chatMemberships = chat.memberships.map((m) => m.user.id)
        if (temporaryNewChat.memberships.length !== chat.memberships.length || !temporaryNewChatMemberships.every((id) => chatMemberships.includes(id))) {
          return
        }
      }

      chat.title = this.getChatTitle(chat)
      this.updateChat({ chatIndex: temporaryNewChatIndex, chat })
      this.selectedChatState = chat
    },
    async updateChatLastMessage({ chatId, message, onEdit }) {
      const chatIndex = this.chatsState.findIndex((c) => Number(c.id) === Number(chatId))

      let chat

      if (chatIndex > -1) {
        if (onEdit && this.chatsState[chatIndex].last_message?.id !== message.id) {
          return
        }
        chat = { ...this.chatsState[chatIndex], last_message: message, last_action_at: message.time, archived: false }
      } else {
        try {
          const { conversation } = await messagesApi.chat(chatId)
          chat = conversation
          chat.title = this.getChatTitle(chat)
        } catch (err) {
          sentryReporter.reportError(err)
        }
      }
      this.updateChat({ chatIndex, chat })
    },
    setChatMessagesLoading(chatMessagesLoading) {
      this.chatMessagesLoadingState = chatMessagesLoading
    },
    setSelectedNewChatContacts(selectedNewChatContacts) {
      this.selectedNewChatContactsState = selectedNewChatContacts
    },
    addSavedMessage({ message, conversationId }) {
      const existing = this.savedMessagesToSendState.find((s) => s.conversationId === conversationId)

      if (existing) {
        existing.message = message
      } else {
        this.savedMessagesToSendState.push({ message, conversationId })
      }
    },
    removeSavedMessage(conversationId) {
      const index = this.savedMessagesToSendState.findIndex((s) => s.conversationId === conversationId)
      if (index > -1) {
        this.savedMessagesToSendState.splice(index, 1)
      }
    },
    async renameSelectedChat(name) {
      this.selectedChatState.name = name
      this.selectedChatState.title = this.getChatTitle(this.selectedChatState)

      if (this.selectedChatState.new) {
        return
      }

      await messagesApi.updateChatName({ conversationId: this.selectedChatState.id, name })
    },
    async leaveChat(chat) {
      if (!chat.new) {
        await messagesApi.leaveConversation(chat.id)
      }

      const chatIndex = this.chatsState.findIndex((c) => c.id === chat.id)
      if (chatIndex > -1) {
        this.chatsState.splice(chatIndex, 1)
      }

      if (this.selectedChatState?.id === chat.id) {
        this.selectedChatState = null
      }
    },
    async archiveSelectedChat() {
      if (!this.selectedChatState.new) {
        await messagesApi.archiveChat(this.selectedChatState.id)
      }

      this.selectedChatState.archived = true
      this.removeSelectedChatFromList()
    },
    removeSelectedChatFromList() {
      const index = this.chatsState.findIndex((c) => c.id === this.selectedChatState.id)
      if (index > -1) {
        this.chatsState.splice(index, 1)
      }

      this.deselectChat()
    },
    setMessageToEdit(message) {
      this.messageToEditState = message
    },
    setMessageToReplyTo(message) {
      this.messageToReplyToState = message
    },
    updateChatData(chat) {
      const chatIndex = this.chatsState.findIndex((c) => c.id === chat.id)

      chat.title = this.getChatTitle(chat)
      if (chatIndex > -1) {
        this.chatsState[chatIndex] = chat
      }

      if (this.selectedChatState?.id === chat.id) {
        this.selectedChatState = chat
      }
    },
    async unarchiveSelectedChat({ skipApi } = {}) {
      if (!skipApi) {
        await messagesApi.unarchiveChat(this.selectedChatState.id)
      }
      this.selectedChatState.archived = false
    },
    setSelectedChatLocked() {
      this.selectedChatState.locked = true
    },
    async markAllChatsAsRead() {
      await messagesApi.markAllConversationsAsRead()

      this.chatsState.forEach((chat) => {
        const currentUserFromChat = chat.memberships.find((m) => m.user.id === useUserSessionStore().user.id)
        currentUserFromChat.last_read_message_id = chat.last_message?.id
      })
    }
  }
})
